import { useEffect, useState } from 'react';
import { useScrollLock } from './useScrollLock';
import type { FaqSection, PlatformSettings } from 'shared';
import { api } from './api';
import { haptic, openTelegram } from './telegram';

let cache: PlatformSettings | null = null;
let faqCache: FaqSection[] | null = null;

/**
 * Кнопка поддержки и справка.
 *
 * Висит поверх всех экранов маленьким кружком: искать помощь в меню человек
 * не станет, а занимать ею постоянную строку интерфейса — расточительно.
 * Раскрывается в лист с контактами и разделом «Справка».
 */
export default function Support() {
  const [settings, setSettings] = useState<PlatformSettings | null>(cache);
  const [faq, setFaq] = useState<FaqSection[]>(faqCache ?? []);
  const [open, setOpen] = useState(false);

  // Общее правило приложения: под открытым слоем страница не двигается.
  useScrollLock(open);
  const [faqOpen, setFaqOpen] = useState<string | null>(null);

  useEffect(() => {
    if (cache) return;
    api
      .settings()
      .then((s) => {
        cache = s;
        setSettings(s);
      })
      .catch(() => {});
  }, []);

  // Справку тянем только при первом раскрытии окна: большинству она не нужна.
  useEffect(() => {
    if (!open || faqCache) return;
    api
      .faq()
      .then((r) => {
        faqCache = r.items;
        setFaq(r.items);
      })
      .catch(() => {});
  }, [open]);

  // Кнопка бесполезна, пока администратор не указал контакт поддержки.
  if (!settings || (!settings.supportTelegram && !settings.supportPhone)) return null;

  const tgLink = settings.supportTelegram
    ? `https://t.me/${settings.supportTelegram}`
    : null;

  return (
    <>
      <button
        className="support-fab"
        aria-label="Поддержка"
        onClick={() => {
          haptic();
          setOpen(true);
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9 9 0 0 1-3.9-.9L3 20.5l1.5-4.4A8.4 8.4 0 0 1 3.6 11.5a8.4 8.4 0 0 1 8.4-8.4 8.4 8.4 0 0 1 9 8.4Z" />
          <path d="M9.6 9.4a2.4 2.4 0 0 1 4.7.6c0 1.6-2.4 2.4-2.4 2.4M12 16.2h.01" />
        </svg>
      </button>

      {open && (
        <div className="sheet-backdrop" onClick={() => setOpen(false)}>
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            <div className="row" style={{ marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Поддержка</h3>
              <button
                className="secondary inline"
                onClick={() => setOpen(false)}
              >
                Закрыть
              </button>
            </div>

            {settings.supportNote && (
              <div className="meta" style={{ marginBottom: 12 }}>
                {settings.supportNote}
              </div>
            )}

            <div className="meta" style={{ marginBottom: 12 }}>
              Время работы: {settings.supportHours}
            </div>

            {tgLink && (
              <button onClick={() => openTelegram(tgLink)}>
                Написать в Telegram
              </button>
            )}

            {settings.supportPhone && (
              <a href={`tel:${settings.supportPhone}`}>
                <button className="secondary" style={{ marginTop: 8 }}>
                  Позвонить {settings.supportPhone}
                </button>
              </a>
            )}

            {settings.faqEnabled && faq.length > 0 && (
              <>
                <div className="divider" />
                <h3>Справка</h3>
                {faq.map((s) => (
                  <div key={s.id} className="faq">
                    <button
                      className="faq__head"
                      onClick={() => setFaqOpen(faqOpen === s.id ? null : s.id)}
                    >
                      <span>{s.title}</span>
                      <span className="faq__sign">{faqOpen === s.id ? '−' : '+'}</span>
                    </button>
                    {faqOpen === s.id && (
                      <div className="faq__body">
                        <ul>
                          {s.items.map((it) => (
                            <li key={it}>{it}</li>
                          ))}
                        </ul>
                        {s.note && <div className="meta">{s.note}</div>}
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/** Настройки нужны и другим экранам — отдаём из того же кэша. */
export function useSettings(): PlatformSettings | null {
  const [s, setS] = useState<PlatformSettings | null>(cache);

  useEffect(() => {
    if (cache) return;
    api
      .settings()
      .then((v) => {
        cache = v;
        setS(v);
      })
      .catch(() => {});
  }, []);

  return s;
}
